import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, CheckCircle2, X } from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * SavedCardBanner — Iter 118 (Feb 2026) dismissible "Save card for one-click
 * boost" prompt on the business dashboard. Hidden under the Founding Business
 * Offer (no card at signup); shown only when the business has not yet saved a
 * card AND has not dismissed the banner.
 *
 * Mechanism: opens a Stripe-hosted Checkout Session in `setup` mode (NO charge).
 * The customer enters card details on Stripe.com, then returns to the
 * dashboard with `?cardSaved=1` query param. We re-check on mount/return and
 * flip to the "Card saved ✓" state with a Remove action.
 */
const STORAGE_KEY = 'savedCardBannerDismissed-v1';

const SavedCardBanner = () => {
  const { t } = useTranslation();
  const [hasCard, setHasCard] = useState(null);  // null=loading
  const [card, setCard] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try { setDismissed(localStorage.getItem(STORAGE_KEY) === '1'); } catch { /* ignored */ }
    refresh();
    // Auto-refresh when user returns from Stripe with ?cardSaved=1
    if (new URLSearchParams(window.location.search).get('cardSaved') === '1') {
      // clear the query string so reload doesn't loop
      const url = new URL(window.location.href);
      url.searchParams.delete('cardSaved');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const refresh = async () => {
    try {
      const res = await axiosInstance.get('/stripe/saved-card');
      setHasCard(!!res.data?.hasCard);
      setCard(res.data?.hasCard ? res.data : null);
    } catch {
      setHasCard(false);
    }
  };

  const saveCard = async () => {
    setBusy(true);
    try {
      const res = await axiosInstance.post('/stripe/create-setup-checkout', {
        originUrl: window.location.origin,
      });
      if (res.data?.checkoutUrl) window.location.assign(res.data.checkoutUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[saved-card] setup checkout failed', e);
      setBusy(false);
    }
  };

  const removeCard = async () => {
    if (!window.confirm(t('savedCard.confirmRemove', 'Remove your saved card? You can add it again any time.'))) return;
    setBusy(true);
    try {
      await axiosInstance.delete('/stripe/saved-card');
      await refresh();
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignored */ }
    setDismissed(true);
  };

  if (hasCard === null) return null;
  if (dismissed && !hasCard) return null;

  if (hasCard && card) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm"
        data-testid="saved-card-banner-saved"
      >
        <CheckCircle2 className="h-5 w-5 text-green-700 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-green-900">
            {t('savedCard.savedTitle', 'Card saved for one-click boosts')}
          </p>
          <p className="text-green-800/80 text-xs leading-snug">
            {(card.brand || '').toUpperCase()} •••• {card.last4} · {t('savedCard.expires', 'Expires')} {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
          </p>
        </div>
        <button
          type="button"
          onClick={removeCard}
          disabled={busy}
          className="text-xs text-green-700 hover:text-green-900 underline disabled:opacity-50"
          data-testid="saved-card-remove-btn"
        >
          {t('savedCard.remove', 'Remove')}
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl text-sm relative"
      data-testid="saved-card-banner"
    >
      <CreditCard className="h-5 w-5 text-purple-700 flex-shrink-0 mt-0.5" />
      <div className="flex-1 pr-7">
        <p className="font-semibold text-purple-900">
          {t('savedCard.promptTitle', 'Save your card for one-click boosts')}
        </p>
        <p className="text-purple-800/80 text-xs leading-snug mt-0.5">
          {t('savedCard.promptBody', "Optional. We won't charge anything now — only when you choose to boost an event or offer.")}
        </p>
      </div>
      <button
        type="button"
        onClick={saveCard}
        disabled={busy}
        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg disabled:opacity-60"
        data-testid="saved-card-save-btn"
      >
        {busy ? t('savedCard.opening', 'Opening…') : t('savedCard.saveCta', 'Save card')}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 text-purple-500 hover:text-purple-800"
        data-testid="saved-card-dismiss-btn"
        aria-label={t('savedCard.dismiss', 'Dismiss')}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default SavedCardBanner;
